import { useState, useRef, useEffect } from "react";
import { View, StyleSheet, Text, Picker, Image } from "react-native";
import { useAppTheme } from "../../hooks/useAppTheme";
import type { ThemeColors } from "../../utils/constants/colors";
import { Menu } from "react-native-paper";
import Icons from "@expo/vector-icons/MaterialCommunityIcons";
import { useHover } from "react-native-web-hooks";
import * as Okta from "../../services/oktaAuthService";
import roleConfig from "../../utils/constants/roles";
import oktaConfig from "../../configs/okta";
import * as Services from "../../utils/common";
import useColorScheme from "../../hooks/useColorScheme";
import { getRole } from "../../services/usersService";
import { getS3SignUrl } from "../../services/s3BucketService";
import { useSelector } from "react-redux";
import { companyModel } from "../../models/companyModel";

import { ConfigurationDispatcher } from "../../redux/dispatcher/configurationDispatcher";
import { actionTypesConfigurations } from "../../redux/constants/actionTypesConfigurations";
import {
  clearSessionStorage,
  setItemInSessionStorage,
} from "../../services/utils/storageService";
import { persistor } from "../../redux/configureStore";
import { useTranslation } from "../../utils/useTranslation";

export default function AvatarMenu(props: any) {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const {
    companies,
    currentCompany,
    currentRole,
    navigation,
    userInfo,
    language,
    themeMode,
  } = useSelector((state: any) => state.configurations);

  const { t } = useTranslation();
  const colorScheme = useColorScheme();
  const outOfBoxRef = useRef(null);
  const settingsRef = useRef(null);
  const logoutRef = useRef(null);
  const switchRef = useRef(null);
  const userManagementRef = useRef(null);
  const orgManagementRef = useRef(null);
  const notificationsRef = useRef(null);
  const isSwitchHovered = useHover(switchRef);
  const isSettingsHovered = useHover(settingsRef);
  const isLogoutHovered = useHover(logoutRef);
  const isUserManagementHovered = useHover(userManagementRef);
  const isOrgManagementHovered = useHover(orgManagementRef);
  const isNotificationHovered = useHover(notificationsRef);
  const [showTeam, setShowTeam] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<string>(
    companies.length ? currentCompany.company_id : ""
  );
  const { loading, notificationCount } = useSelector(
    (state: any) => state.notification
  );

  const setHasAvatarMenu = (data: boolean) => {
    ConfigurationDispatcher(actionTypesConfigurations.HasAvatarMenu, data);
  };

  const logoutHandler = () => {
    Okta.deleteSession().then(() => {
      clearSessionStorage();
      persistor.purge();
      window.location.href = `${oktaConfig.api.redirectUri}`;
    });
  };

  const selectedView = (selectedTitle: string) => {
    return (
      <Text style={styles.menuItemText}>{selectedTitle}</Text>
    );
  };

  const checkOutsideClick = (ref: any) => {
    useEffect(() => {
      let isMounted = true;
      function handleClickOutside(event: any) {
        // If click is inside the menu itself, do nothing
        if (ref.current && ref.current.contains(event.target)) return;

        // If click is inside the avatar button trigger, do nothing (let the button's onPress toggle it)
        if (props.buttonRef?.current && props.buttonRef.current.contains(event.target)) return;

        // Otherwise it's a click outside, so close the menu
        if (isMounted) {
          setHasAvatarMenu(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        isMounted = false;
      };
    }, [ref]);
  };

  const pickerItemView = () => {
    const pickerItems = companies.map((company: companyModel) => (
      <Picker.Item
        key={Services.makeid(4)}
        label={company.company_name.toUpperCase()}
        value={company.company_id}
      />
    ));
    return pickerItems;
  };

  const pickerHandler = (itemValue: string) => {
    setSelectedValue(itemValue);

    const selectedCompanyId = itemValue;
    var company = companies.filter((comp: companyModel) => {
      return [selectedCompanyId].indexOf(comp.company_id) > -1;
    });
    ConfigurationDispatcher(
      actionTypesConfigurations.CurrentCompany,
      company[0]
    );

    setItemInSessionStorage(
      "company-context",
      JSON.stringify({
        company_id:
          typeof company[0] !== "undefined"
            ? company[0].company_id
            : "0000000001",
        company_name:
          typeof company[0] !== "undefined"
            ? company[0].company_name
            : "Multisteps",
      })
    );

    const companyId = company[0].company_id; //Multisteps Company ID
    getS3SignUrl(
      "multisteps-go-public-image",
      `logo/${companyId}.png`,
      "getObject",
      "image/*"
    ).then((data) => {
      setItemInSessionStorage("logo-context", data?.signedUrl);
    });

    var company = companies.filter((comp: companyModel) => {
      return [selectedValue].indexOf(comp.company_id) > -1;
    });
    getRole(company[0].company_id).then((data) => {
      ConfigurationDispatcher(
        actionTypesConfigurations.CurrentRole,
        data?.role
      );
    });
  };

  checkOutsideClick(outOfBoxRef);

  useEffect(() => {
    let isMounted = true;
    return () => {
      isMounted = false;
    };
  }, []);

  const handleLanguageChange = (language: any) => {
    ConfigurationDispatcher(actionTypesConfigurations.Language, language);
    setItemInSessionStorage("language-context", language);
  };

  const handleThemeChange = (mode: string) => {
    ConfigurationDispatcher(actionTypesConfigurations.ThemeMode, mode);
    setItemInSessionStorage("theme-context", mode);
  };

  return (
    <View ref={outOfBoxRef} style={styles.container}>
      {/* Company Logo */}
      <View style={styles.logoContainer}>
        <Image
          source={require("../../assets/Multisteps_Logo_Blue.png")}
          style={styles.logo}
          resizeMode="contain"
        />
      </View>

      <View style={styles.divider} />

      {/* User Header Section */}
      <View style={styles.userHeader}>
        <View style={styles.userAvatarCircle}>
          <Text style={styles.userAvatarText}>
            {userInfo?.display_name && userInfo.display_name !== "undefined"
              ? userInfo.display_name.charAt(0).toUpperCase()
              : "U"}
          </Text>
        </View>
        <View style={styles.userHeaderInfo}>
          <Text style={styles.userName}>
            {userInfo?.display_name && userInfo.display_name !== "undefined"
              ? userInfo.display_name
              : "User"}
          </Text>
          {currentCompany && (
            <Text style={styles.userRole}>
              {currentRole?.charAt(0).toUpperCase() + currentRole?.slice(1).toLowerCase()}
            </Text>
          )}
        </View>
      </View>

      {currentCompany && showTeam ? (
        <View style={styles.teamRow}>
          <Text style={styles.teamLabel}>TEAM</Text>
          <Text style={styles.teamValue}>
            {userInfo.team.toUpperCase()}
          </Text>
        </View>
      ) : !props.currentCompany && showTeam ? (
        <View style={styles.teamRow}>
          <Text style={styles.teamLabel}>ROLE</Text>
          <Text style={styles.teamValue}>
            {roleConfig.roles.standard}
          </Text>
        </View>
      ) : (
        <View />
      )}

      <View style={styles.divider} />

      {/* Menu Items Section */}
      <View style={styles.menuSection}>
        <View
          ref={settingsRef}
          style={[
            styles.menuItem,
            isSettingsHovered && styles.menuItemHover,
            props.title === "User Settings" && styles.menuItemActive,
          ]}
        >
          <View style={styles.menuIconWrapper}>
            <Icons name="account-outline" size={18} color={colors.textSecondary} />
          </View>
          <Menu.Item
            onPress={() => (
              setHasAvatarMenu(false),
              navigation.navigate("UserSettings", { screen: "Profile" })
            )}
            title={selectedView(t("accountSettings"))}
          />
        </View>

        {currentCompany &&
          (currentRole === roleConfig.roles.admin ||
            currentRole === roleConfig.roles.owner) ? (
          <View
            ref={userManagementRef}
            style={[
              styles.menuItem,
              isUserManagementHovered && styles.menuItemHover,
              props.title === "Users Management" && styles.menuItemActive,
            ]}
          >
            <View style={styles.menuIconWrapper}>
              <Icons name="account-group-outline" size={18} color={colors.textSecondary} />
            </View>
            <Menu.Item
              onPress={() => (
                setHasAvatarMenu(false),
                navigation.navigate("OrgSettings", { screen: "Users" })
              )}
              title={selectedView(t("usersManagement"))}
            />
          </View>
        ) : (
          <View ref={userManagementRef} />
        )}

        {currentCompany &&
          (currentRole === roleConfig.roles.admin ||
            currentRole === roleConfig.roles.owner) ? (
          <View
            ref={orgManagementRef}
            style={[
              styles.menuItem,
              isOrgManagementHovered && styles.menuItemHover,
              props.title === "Organization Management" && styles.menuItemActive,
            ]}
          >
            <View style={styles.menuIconWrapper}>
              <Icons name="office-building-outline" size={18} color={colors.textSecondary} />
            </View>
            <Menu.Item
              onPress={() => (
                setHasAvatarMenu(false),
                navigation.navigate("OrgSettings", { screen: "Company" })
              )}
              title={selectedView(t("organizationSettings"))}
            />
          </View>
        ) : (
          <View ref={orgManagementRef} />
        )}
      </View>

      <View style={styles.divider} />

      {/* Preferences Section */}
      <View style={styles.menuSection}>
        {currentCompany ? (
          <View
            ref={switchRef}
            style={[
              styles.menuItem,
              props.title === "Organization Switch" && styles.menuItemActive,
            ]}
          >
            <View style={styles.menuIconWrapper}>
              <Icons name="swap-horizontal" size={18} color={colors.textSecondary} />
            </View>
            <Picker
              style={styles.pickerStyle}
              selectedValue={selectedValue}
              onValueChange={(itemValue, itemIndex) => pickerHandler(itemValue)}
            >
              {pickerItemView()}
            </Picker>
          </View>
        ) : (
          <View ref={switchRef} />
        )}

        {/* Theme Picker */}
        <View style={styles.menuItem}>
          <View style={styles.menuIconWrapper}>
            <Icons name="theme-light-dark" size={18} color={colors.textSecondary} />
          </View>
          <Picker
            style={styles.pickerStyle}
            selectedValue={themeMode || "System Theme"}
            onValueChange={(value: any) => handleThemeChange(value)}
          >
            <Picker.Item label="System Theme" value="system" />
            <Picker.Item label="Light Mode" value="light" />
            <Picker.Item label="Dark Mode" value="dark" />
          </Picker>
        </View>

        {/* Language Picker */}
        <View style={styles.menuItem}>
          <View style={styles.menuIconWrapper}>
            <Icons name="translate" size={18} color={colors.textSecondary} />
          </View>
          <Picker
            style={styles.pickerStyle}
            selectedValue={language}
            onValueChange={(value: any) => handleLanguageChange(value)}
          >
            <Picker.Item label={t("english")} value="en" />
            <Picker.Item label={t("chinese")} value="zh" />
          </Picker>
        </View>
      </View>

      <View style={styles.divider} />

      {/* Logout Section */}
      <View style={styles.menuSection}>
        <View
          ref={logoutRef}
          style={[
            styles.menuItem,
            isLogoutHovered && styles.logoutHover,
            props.title === "Logout" && styles.menuItemActive,
          ]}
        >
          <View style={styles.menuIconWrapper}>
            <Icons name="logout" size={18} color={isLogoutHovered ? colors.danger : colors.textSecondary} />
          </View>
          <Menu.Item
            onPress={() => logoutHandler()}
            title={
              <Text style={[styles.menuItemText, isLogoutHovered && { color: "#dc2626" }]}>
                {t("logout")}
              </Text>
            }
          />
        </View>
      </View>
    </View>
  );
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      minWidth: 260,
      paddingVertical: 8,
    },
    // Company Logo
    logoContainer: {
      paddingHorizontal: 16,
      paddingVertical: 10,
      alignItems: "center",
    },
    logo: {
      width: 110,
      height: 28,
    },
    // User Header
    userHeader: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 16,
      paddingVertical: 14,
    },
    userAvatarCircle: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: "#4f46e5",
      justifyContent: "center",
      alignItems: "center",
    },
    userAvatarText: {
      color: "#ffffff",
      fontSize: 16,
      fontWeight: "700",
    },
    userHeaderInfo: {
      marginLeft: 12,
      flexShrink: 1,
    },
    userName: {
      fontSize: 15,
      fontWeight: "600",
      color: colors.text,
      letterSpacing: 0.1,
    },
    userRole: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 2,
      letterSpacing: 0.2,
    },
    // Team Row
    teamRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 16,
      paddingVertical: 6,
    },
    teamLabel: {
      fontSize: 10,
      fontWeight: "700",
      color: colors.textMuted,
      letterSpacing: 1,
      marginRight: 8,
    },
    teamValue: {
      fontSize: 12,
      fontWeight: "600",
      color: colors.text,
    },
    // Divider
    divider: {
      height: 1,
      backgroundColor: colors.borderSubtle,
      marginHorizontal: 12,
      marginVertical: 4,
    },
    // Menu Section
    menuSection: {
      paddingVertical: 4,
    },
    // Menu Item
    menuItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingLeft: 12,
      marginHorizontal: 8,
      borderRadius: 8,
    },
    menuIconWrapper: {
      width: 28,
      alignItems: "center",
      justifyContent: "center",
    },
    menuItemText: {
      fontSize: 13,
      color: colors.text,
      fontWeight: "500",
      letterSpacing: 0.1,
    },
    menuItemHover: {
      backgroundColor: colors.borderSubtle,
    },
    menuItemActive: {
      backgroundColor: colors.primaryBg,
    },
    logoutHover: {
      backgroundColor: colors.dangerBg,
    },
    // Picker
    pickerStyle: {
      fontSize: 13,
      fontWeight: "500",
      borderWidth: 0,
      borderColor: "transparent",
      backgroundColor: "transparent",
      color: colors.text,
      marginLeft: 4,
      paddingVertical: 8,
    },
    // Badge
    badge: {
      position: "absolute",
      top: -5,
      right: -6,
      backgroundColor: colors.danger,
      borderRadius: 8,
      paddingHorizontal: 4,
      minWidth: 16,
      height: 16,
      justifyContent: "center",
      alignItems: "center",
      zIndex: 10,
      borderWidth: 2,
      borderColor: "#ffffff",
    },
    badgeText: {
      color: "#ffffff",
      fontSize: 9,
      fontWeight: "700",
      textAlign: "center",
    },
  });
}
